import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
  type ChartOptions,
} from "chart.js";
import { BarChart3 } from "lucide-react";
import { useMemo } from "react";
import { Bar } from "react-chartjs-2";

import { SectionCard } from "../dashboard/DashboardPrimitives";
import {
  bucketLabel,
  formatShare,
  selfHostedShare,
  windowLabel,
} from "./fleetWindow";
import type { H3History } from "./h3Fleet";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

/** One colour per series, the same on light and dark (canvas cannot read CSS variables). */
const SERIES_COLORS = {
  self_hosted: "#059669",
  runpod: "#2a78d6",
  failed: "#e11d48",
} as const;

const OPTIONS: ChartOptions<"bar"> = {
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  scales: {
    x: {
      stacked: true,
      grid: { display: false },
      ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12 },
    },
    y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
  },
  plugins: {
    legend: { position: "bottom", labels: { boxWidth: 10 } },
    tooltip: { mode: "index", intersect: false },
  },
};

/** Clips finished per time bucket, stacked by where they rendered, plus the failures. */
export function ClipsOverTimeCard({ history }: { history: H3History }) {
  const { series, bucket_seconds: bucketSeconds } = history;
  const data = useMemo(() => {
    const points = series ?? [];
    const bucket = bucketSeconds ?? 3600;
    return {
      labels: points.map((point) => bucketLabel(point.start, bucket)),
      datasets: [
        {
          label: "Self-hosted",
          data: points.map((point) => point.self_hosted),
          backgroundColor: SERIES_COLORS.self_hosted,
        },
        {
          label: "RunPod",
          data: points.map((point) => point.runpod),
          backgroundColor: SERIES_COLORS.runpod,
        },
        {
          label: "Failed",
          data: points.map((point) => point.failed),
          backgroundColor: SERIES_COLORS.failed,
        },
      ],
    };
  }, [series, bucketSeconds]);
  const label = windowLabel(history.window_hours);
  const share = selfHostedShare(history.by_plane);
  return (
    <SectionCard
      title={`Clips finished, last ${label}`}
      icon={<BarChart3 className="size-4" />}
      action={
        <span className="text-xs text-muted-foreground">
          {history.completed ?? 0} delivered · {history.failed ?? 0} failed
          {share !== null ? ` · ${formatShare(share)} on your GPUs` : ""}
        </span>
      }
    >
      {(history.total ?? 0) === 0 ? (
        <p className="text-sm text-muted-foreground">
          No H3 clip finished in this environment in the last {label}.
        </p>
      ) : (
        <div
          className="h-56"
          role="img"
          aria-label="Clips finished per time bucket, by plane"
        >
          <Bar data={data} options={OPTIONS} />
        </div>
      )}
    </SectionCard>
  );
}
